import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { configureAccountStorage } from '@my/platform/account';
import { configureHttp } from '@my/platform/http';
import { consumeSupportHandoff } from '@my/platform/support';
import { App } from '@/App';
import { loadConfig } from '@/configuration/config';
import { initLocalisation } from '@/localisation';
import '@/styles.css';

/**
 * Suite-app identifier for this build. Used as:
 *  - the localStorage namespace for per-app caches (`my:tms:*`).
 *  - the Auth0 PostLogin Action's `subscription` parameter (gates the JWT
 *    to TMS-licensed accounts and denies users who don't have it).
 *
 * Each suite app (`tms`, `directory`, `depot`, `expenses`) hardcodes its
 * own. Must match the BE's app id and the Auth0 Action's accepted set.
 */
const APP_CODE = 'tms';

async function bootstrap() {
  // Load /config.json BEFORE rendering so Auth0Provider, axios, etc. all see
  // a populated config from their first render. If this fails we render a
  // hard error rather than mount a half-broken tree.
  let cfg;
  try {
    cfg = await loadConfig();
  } catch (err) {
    renderConfigError(err);
    return;
  }

  // Stamp the app code into the platform's account-storage helpers so
  // localStorage keys are namespaced (`my:tms:account_id`) rather than
  // colliding generically across the suite. Must run before any provider
  // mount that reads / writes the cache.
  configureAccountStorage(APP_CODE);

  // Wire the shared http transport before any component can fire a request.
  // The token getter is wired separately by AuthTokenBridge once Auth0 mounts.
  configureHttp({ baseUrl: cfg.apiUrl, appKey: cfg.appKey });

  // A support session handed over from My Hub ("open this customer in
  // TMS") travels in the URL, because the Hub is a different origin and
  // its sessionStorage cannot reach this one. Adopt it before the first
  // request fires, or that request goes out scoped to no account.
  consumeSupportHandoff();

  // Init i18next with bundled English resources before first render so
  // `useTranslation()` callers never see an uninitialised t-function. The
  // runtime language gets switched to the org's `languageCode` later by
  // `SyncLanguageWithOrg`, once the user authenticates and the org loads.
  await initLocalisation();

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Missing #root element');

  createRoot(rootEl).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

function renderConfigError(err: unknown) {
  const rootEl = document.getElementById('root');
  if (!rootEl) return;
  const msg = err instanceof Error ? err.message : String(err);
  rootEl.innerHTML = `
    <div style="font-family:system-ui;padding:2rem;color:#991b1b;background:#fef2f2;height:100vh">
      <h1 style="margin-bottom:0.5rem;font-size:1.25rem;font-weight:600">Failed to load /config.json</h1>
      <p>${msg}</p>
    </div>
  `;
}

void bootstrap();
